import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Stack,
    TableCell,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimelineIcon from '@mui/icons-material/Timeline';
import useNotify from '../../hooks/useNotify';
import { doSwaggerCall } from '../../hooks/useApi';
import PageWithTitle from '../../components/atom/PageWithTitle';
import GenericTable from '../../components/atom/GenericTable';
import { PAYMENTS_OF_TICKET_COLUMNS } from '../../helpers/tableColumnLists';
import { handleNavigateBack } from '../../helpers/handleDetailsPageFunctions';
import handleNavigation from '../../helpers/handleNavigation';
import CloseParkingDialog from './components/CloseParkingDialog';
import TicketTimeline from '../../components/parking/TicketTimeline';
import { findPlateVisit, liveParkingUrl } from '../../helpers/parkingEvents';
import { PaymentStatus, paymentAmountLabel } from '../payments/paymentFormat';
import {
    ParkingStatusChip,
    PaidChip,
    TicketAmount,
    closedByLabel,
    formatDateTime,
    formatDuration,
    formatFreeTime,
    formatHuf,
    parseDate,
    visitEnd,
    visitExit,
} from './parkingFormat';

const NAVIGATE_BACK_URL = '/parkings';
// ticket ids are plate based, so every payment of the plate is loaded at once and split by the current entry
const PAYMENTS_LIMIT = 200;

function Field({ label, children }) {
    return (
        <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
                {label}
            </Typography>
            <Typography variant="body1" component="div">
                {children}
            </Typography>
        </Box>
    );
}

function Card({ title, children }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title}
            </Typography>
            {children}
        </Paper>
    );
}

function ParkingDetailsPage() {
    const { parkingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { notifyError, notifySuccess } = useNotify();
    const [ticket, setTicket] = useState(null);
    const [refreshStatus, setRefreshStatus] = useState(true);
    const [parkingLoading, setParkingLoading] = useState(true);
    const [payments, setPayments] = useState(null);
    const [paymentsLoading, setPaymentsLoading] = useState(true);
    const [closeOpen, setCloseOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [ticketUser, setTicketUser] = useState(null);

    const loadParking = useCallback(() => {
        setParkingLoading(true);
        doSwaggerCall('ParkingTickets', 'getParkingTicket', { id: parkingId })
            .then((res) => {
                setTicket(res.ticket);
                setRefreshStatus(res.refreshStatus);
                if (res.refreshStatus) setRefreshedAt(new Date());
                setParkingLoading(false);
            })
            .catch((err) => {
                if (err?.status === 404) {
                    // not parked with the app (no ticket in our DB): the parking system's events tell the visit
                    return findPlateVisit(parkingId).then((visit) => {
                        setTicket(visit);
                        setParkingLoading(false);
                    });
                }
                throw err;
            })
            .catch((err) => {
                setParkingLoading(false);
                notifyError(err);
                navigate(NAVIGATE_BACK_URL);
            });
    }, [parkingId]);

    const loadPayments = useCallback(() => {
        setPaymentsLoading(true);
        doSwaggerCall('Payments', 'getPayments', {
            offset: 0,
            limit: PAYMENTS_LIMIT,
            searchTicketId: parkingId,
        })
            .then((res) => {
                setPayments(res);
                setPaymentsLoading(false);
            })
            .catch((err) => {
                notifyError(err);
                setPaymentsLoading(false);
            });
    }, [parkingId]);

    useEffect(() => {
        loadParking();
    }, [loadParking]);

    // ask the parking system again (e.g. the ticket was paid at the physical pay station, not in the app)
    const refresh = () => {
        setRefreshing(true);
        doSwaggerCall('ParkingTickets', 'getParkingTicket', { id: parkingId })
            .then((res) => {
                setTicket(res.ticket);
                setRefreshStatus(res.refreshStatus);
                if (res.refreshStatus) {
                    setRefreshedAt(new Date());
                    notifySuccess('Frissítve a parkoló rendszerből');
                }
                loadPayments();
            })
            .catch(notifyError)
            .finally(() => setRefreshing(false));
    };

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    // the ticket only carries the user_id, resolve it to name + e-mail
    const ticketUserId = ticket?.user_id;
    useEffect(() => {
        setTicketUser(null);
        if (!ticketUserId) return;
        doSwaggerCall('Users', 'getUser', { id: ticketUserId })
            .then((res) =>
                setTicketUser({
                    name: [res.lastname, res.firstname].filter(Boolean).join(' '),
                    email: res.email,
                })
            )
            .catch(() => setTicketUser(null));
    }, [ticketUserId]);

    const paymentRow = (payment) => ({
        id: payment.orderref,
        createdAt: formatDateTime(payment.created_at),
        amount: paymentAmountLabel(payment, formatHuf(payment.amount)),
        email: payment.email,
        orderref: payment.orderref,
        status: <PaymentStatus payment={payment} />,
        userId: payment.user_id,
        detailsIcon: (
            <TableCell align="right" sx={{ width: '5%' }}>
                <Tooltip title="Részletek">
                    <IconButton
                        onClick={() => {
                            handleNavigation(location, (stateObj) => {
                                navigate(`/payments/details/${payment.orderref}`, stateObj);
                            });
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
            </TableCell>
        ),
    });

    // the same plate has earlier visits too: payments started since this entry belong to this parking
    const entry = parseDate(ticket?.entryTime);
    const isCurrent = (payment) => !entry || parseDate(payment.created_at) >= entry;
    const allPayments = payments?.payments || [];
    const currentRows = allPayments.filter(isCurrent).map(paymentRow);
    const earlierRows = allPayments.filter((p) => !isCurrent(p)).map(paymentRow);

    if (parkingLoading || !ticket) {
        return (
            <Grid item xs={12}>
                <CircularProgress />
            </Grid>
        );
    }

    const closed = !!ticket.closedAt;
    const { fromEvents } = ticket;
    const headline = (
        <Stack direction="row" spacing={1}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => handleNavigateBack(navigate, location, NAVIGATE_BACK_URL)}
            >
                Vissza
            </Button>
            <Tooltip title="A rendszám eseményei az élő forgalom nézetben (a parkolás idejére)">
                <Button
                    variant="outlined"
                    startIcon={<TimelineIcon />}
                    onClick={() =>
                        handleNavigation(location, (stateObj) =>
                            navigate(liveParkingUrl(ticket.id, ticket.entryTime), stateObj)
                        )
                    }
                >
                    Élő forgalom
                </Button>
            </Tooltip>
            {!closed && !fromEvents && (
                <Tooltip
                    title={
                        refreshedAt
                            ? `Utoljára frissítve a parkoló rendszerből: ${refreshedAt.toLocaleTimeString(
                                  'hu-HU'
                              )}`
                            : 'Adatok újralekérése a parkoló rendszerből'
                    }
                >
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={
                                refreshing ? <CircularProgress size={16} /> : <RefreshIcon />
                            }
                            onClick={refresh}
                            disabled={refreshing}
                        >
                            Frissítés
                        </Button>
                    </span>
                </Tooltip>
            )}
            {!closed && !fromEvents && (
                <Tooltip title="Ingyenes kifizetés a parkoló rendszerben (az autó kihajthat) és lezárás az appban">
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<LockIcon />}
                        onClick={() => setCloseOpen(true)}
                    >
                        Kiengedés és lezárás
                    </Button>
                </Tooltip>
            )}
        </Stack>
    );

    return (
        <PageWithTitle title="Parkolás" headline={headline}>
            <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                    >
                        {ticket.id}
                    </Typography>
                    {ticket.status && <ParkingStatusChip ticket={ticket} size="medium" />}
                    {fromEvents ? (
                        <Chip
                            size="medium"
                            variant="outlined"
                            color={ticket.paidAmount ? 'success' : 'default'}
                            label={
                                ticket.paidAmount
                                    ? `${formatHuf(ticket.paidAmount)} fizetve (nem appban)`
                                    : 'Nincs fizetés az eseményekben'
                            }
                        />
                    ) : (
                        <PaidChip ticket={ticket} size="medium" />
                    )}
                </Stack>
            </Grid>

            {fromEvents && (
                <Grid item xs={12}>
                    <Alert severity={ticket.eventCount ? 'info' : 'warning'}>
                        {ticket.eventCount
                            ? 'Nem az appal parkolt, nincs jegy a Lurdy rendszerében: az adatok a parkoló rendszer eseményeiből (élő forgalom archívum) származnak.'
                            : `Nincs jegy a Lurdy rendszerében, és az elmúlt ${ticket.searchedDays} nap eseményei között sem szerepel ez a rendszám / jegy.`}
                        {ticket.eventCount > 0 &&
                            !ticket.entryTime &&
                            ` A behajtás nincs meg az elmúlt ${ticket.searchedDays} nap eseményei között.`}
                    </Alert>
                </Grid>
            )}

            {closed && (
                <Grid item xs={12}>
                    <Alert severity="warning" icon={<LockIcon />}>
                        <AlertTitle>
                            Kézzel lezárva – {formatDateTime(ticket.closedAt)},{' '}
                            {closedByLabel(ticket)}
                            {ticket.closedBy?.email && ticket.closedBy?.name
                                ? ` (${ticket.closedBy.email})`
                                : ''}
                        </AlertTitle>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {ticket.closeComment}
                        </Typography>
                    </Alert>
                </Grid>
            )}
            {!closed && !fromEvents && !refreshStatus && (
                <Grid item xs={12}>
                    <Alert severity="info">
                        Nem sikerült frissíteni az adatokat a parkoló rendszerből, az utolsó ismert
                        állapotot látod.
                    </Alert>
                </Grid>
            )}

            <Grid item xs={12} md={4}>
                <Card title="Időpontok">
                    <Field label="Behajtás">{formatDateTime(ticket.entryTime)}</Field>
                    <Field label="Kihajtás">{formatDateTime(visitExit(ticket))}</Field>
                    <Field label="Időtartam">
                        {formatDuration(ticket.entryTime, visitEnd(ticket))}
                        {!visitEnd(ticket) && ticket.entryTime ? ' (folyamatban)' : ''}
                    </Field>
                </Card>
            </Grid>
            <Grid item xs={12} md={4}>
                <Card title="Fizetés">
                    {fromEvents ? (
                        <>
                            <Field label="Fizetve a parkoló rendszerben">
                                {formatHuf(ticket.paidAmount)}
                            </Field>
                            <Field label="Kedvezmények">{ticket.appliedDiscounts} db</Field>
                            <Field label="Jegy típusa">{ticket.kind || '–'}</Field>
                        </>
                    ) : (
                        <>
                            <Field label="Fizetendő összeg">
                                <TicketAmount amount={ticket.amount} variant="body1" />
                            </Field>
                            <Field label="Kedvezmények">{ticket.appliedDiscounts || 0} db</Field>
                            <Field label="Szabad idő behajtás / fizetés után">
                                {formatFreeTime(ticket.freeTimeAfterEntry)} /{' '}
                                {formatFreeTime(ticket.freeTimeAfterPayment)}
                            </Field>
                        </>
                    )}
                </Card>
            </Grid>
            <Grid item xs={12} md={4}>
                <Card title="Felhasználó">
                    {ticket.user_id ? (
                        <>
                            <Field label="Név">{ticketUser?.name || ticket.userName || '–'}</Field>
                            <Field label="E-mail">
                                {ticketUser?.email || ticket.userEmail || '–'}
                            </Field>
                            <Button
                                size="small"
                                sx={{ mt: 1 }}
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/users/edit/${ticket.user_id}`)}
                            >
                                Felhasználó adatai (#{ticket.user_id})
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {fromEvents
                                ? 'Nem az appal parkolt, nincs felhasználóhoz rendelve.'
                                : 'Nincs felhasználóhoz rendelve.'}
                        </Typography>
                    )}
                </Card>
            </Grid>

            <Grid item xs={12}>
                <TicketTimeline ticket={ticket} payments={allPayments} />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Fizetések ehhez a parkoláshoz
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    A behajtás ({formatDateTime(ticket.entryTime)}) óta indított fizetések
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Paper
                    variant="outlined"
                    sx={
                        currentRows.length
                            ? {
                                  borderColor: 'primary.main',
                                  borderWidth: 2,
                                  bgcolor: 'action.hover',
                              }
                            : undefined
                    }
                >
                    {paymentsLoading || currentRows.length ? (
                        <GenericTable
                            rows={currentRows}
                            loading={paymentsLoading}
                            paging={false}
                            columns={PAYMENTS_OF_TICKET_COLUMNS}
                            cellPadding="normal"
                            size="small"
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            Ehhez a parkoláshoz nem indult fizetés.
                        </Typography>
                    )}
                </Paper>
            </Grid>

            {!paymentsLoading && earlierRows.length > 0 && (
                <>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            Korábbi parkolások fizetései ezzel a rendszámmal
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            A jegyazonosító rendszám alapú, ezek a fizetések a behajtás előtt
                            indultak, nem ehhez a parkoláshoz tartoznak.
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper variant="outlined">
                            <GenericTable
                                rows={earlierRows}
                                paging={false}
                                columns={PAYMENTS_OF_TICKET_COLUMNS}
                                cellPadding="normal"
                                size="small"
                            />
                        </Paper>
                    </Grid>
                </>
            )}

            <CloseParkingDialog
                ticketId={ticket.id}
                open={closeOpen}
                onClose={() => setCloseOpen(false)}
                onClosed={(res) => {
                    setCloseOpen(false);
                    setTicket(res.ticket);
                    loadPayments();
                }}
            />
        </PageWithTitle>
    );
}

export default ParkingDetailsPage;
